import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Shield, Briefcase, User, Edit2, Trash2, Key, Eye, Mail } from 'lucide-react';

const ROLE_AVATAR_BG = { admin: 'bg-red-100', employee: 'bg-blue-100' };
const roleAvatarBg = (role) => ROLE_AVATAR_BG[role] || 'bg-slate-100';

const RoleBadge = ({ role }) => {
  const roleConfig = {
    admin: { icon: Shield, color: 'bg-red-100 text-red-700', label: 'Admin' },
    employee: { icon: Briefcase, color: 'bg-blue-100 text-blue-700', label: 'Employee' },
    customer: { icon: User, color: 'bg-slate-100 text-slate-700', label: 'Customer' }
  };

  const config = roleConfig[role] || roleConfig.customer;
  const Icon = config.icon;

  return (
    <Badge className={config.color}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
};

const UserTable = ({
  users,
  clients,
  currentUserId,
  onEdit,
  onDelete,
  onResetPassword,
  onViewClients
}) => {
  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || clientId;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="users-table">
        <thead className="bg-slate-50">
          <tr>
            <th className="p-3 text-left text-sm font-medium text-slate-600">User</th>
            <th className="p-3 text-left text-sm font-medium text-slate-600">Role</th>
            <th className="p-3 text-left text-sm font-medium text-slate-600">Assigned Clients</th>
            <th className="p-3 text-left text-sm font-medium text-slate-600">Status</th>
            <th className="p-3 text-right text-sm font-medium text-slate-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {users.map(user => (
            <tr 
              key={user.id} 
              className="hover:bg-slate-50 transition-colors"
              data-testid={`user-row-${user.id}`}
            >
              <td className="p-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${roleAvatarBg(user.role)}`}>
                    <span className="text-lg font-medium">
                      {user.full_name?.charAt(0) || user.email.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{user.full_name || 'No Name'}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {user.email}
                    </p>
                  </div>
                </div>
              </td>
              <td className="p-3">
                <RoleBadge role={user.role} />
              </td>
              <td className="p-3">
                {user.role === 'admin' ? (
                  <Badge className="bg-purple-100 text-purple-800 cursor-default">
                    <Shield className="w-3 h-3 mr-1" />
                    All clients
                  </Badge>
                ) : (
                  <div className="flex items-center gap-2">
                    {user.assigned_client_ids?.length > 0 ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => onViewClients(user)}
                        data-testid={`view-clients-${user.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        {user.assigned_client_ids.length} client{user.assigned_client_ids.length !== 1 ? 's' : ''}
                      </Button>
                    ) : (
                      <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50">
                        No clients assigned
                      </Badge>
                    )}
                  </div>
                )}
              </td>
              <td className="p-3">
                <Badge variant={user.is_active ? 'default' : 'secondary'}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </td>
              <td className="p-3 text-right">
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onEdit(user)}
                    data-testid={`edit-user-${user.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onResetPassword(user.id)}
                    data-testid={`reset-pwd-${user.id}`}
                  >
                    <Key className="w-4 h-4" />
                  </Button>
                  {user.id !== currentUserId && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => onDelete(user.id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;
